/* eslint-disable global-require */
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "antd";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Program } from "types";
import { useMutation } from "@apollo/client";

// graphql
import STUDENT_REGISTRATION from "graphql/mutations/StudentRegistration";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";

import useErrors from "hooks/useErrors";
import ErrorAlerting from "common/error_alerting";
import useStripeSubscription from "hooks/useStripeSubscription";

// custom components
import { Options } from "scroll-into-view-if-needed";
import { authenticate } from "utils/auth";
import { StripeCardElement } from "@stripe/stripe-js";
import { setLanguageDataVar } from "cache";
import { CLASS_TYPES } from "utils/constants";
import RegistrationForm from "./RegistrationForm";
import SelectProgramForm from "./SelectProgramForm";
import PaymentForm from "./PaymentForm";
import Confirmation from "./Confirmation";
import NewSubscriptionInvoice from "./NewSubscriptionInvoice";
import ReviewPanel from "./ReviewPanel";

// styling
import { CustomStep, CustomSteps, PageContent } from "./RegistrationStyle";

type registrationFormValues = {
  locale?: string;
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  confirmPassword: string;
  currentLevelId: number;
  currentStudyLanguage: string;
  preference?: string;
  agreedPrivacyPolicy?: boolean;
  sendTips?: boolean;
  spokenOnly?: boolean;
};

type parsedRegistrationFormValues = {
  locale?: string;
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  currentLevelId: number;
  currentStudyLanguage: string;
  sendTips?: boolean;
  spokenOnly?: boolean;
};

const parseRegistrationFormValues = (
  values: registrationFormValues
): parsedRegistrationFormValues => {
  // Have to disable eslint rule to remove properties from object this way
  const {
    confirmPassword, // eslint-disable-line @typescript-eslint/no-unused-vars
    preference, // eslint-disable-line @typescript-eslint/no-unused-vars
    agreedPrivacyPolicy, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...parsedValues
  } = values;

  return parsedValues;
};

function RegistrationCard(props: any) {
  const { setLoggedIn } = props;

  const { search } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const stripe = useStripe();
  const elements = useElements();
  const { t, i18n } = useTranslation("registration");
  const { errors, handleError, closeError } = useErrors();

  // STATE MANAGEMENT
  const [step, setStep] = useState(0);
  const [currentLanguageId, setCurrentLanguageId] = useState<
    string | undefined
  >(undefined);

  // for loading state of submit butons
  const [loading, setLoading] = useState(false); // FIXME: Remove this
  const [programSelVisible, setProgramSelVisible] = useState<boolean>(false);

  // This is for styling the chosen program.
  // As well as persisting the program choice from step 1 to step 2.
  const [chosenProgram, setChosenProgram] = useState<Program>({
    id: "",
    name: "",
    price: 0,
    filter: [],
    currency: "",
    credits: 0,
    costCategory: "",
    type: "",
  });

  // MUTATIONS AND QUERIES
  const [register] = useMutation(STUDENT_REGISTRATION);
  const [updateMySettings] = useMutation(UPDATE_PROFILE);
  // const [
  //   createSubscription,
  //   { data: createSubscriptionResult, loading: createSubscriptionLoading },
  // ] = useMutation(CREATE_SUBSCRIPTION);

  const {
    createSubscription,
    loading: createSubscriptionLoading,
    data: createSubscriptionResult,
  } = useStripeSubscription();

  // Set locale input when changed language
  i18n.on("languageChanged", (lng) =>
    form.setFieldsValue({
      locale: lng,
    })
  );

  const parseAdInfo = React.useCallback(() => {
    const gclid = new URLSearchParams(search).get("gclid");
    const fbpixel = new URLSearchParams(search).get("fbpixel");
    if (gclid) return `gclid ${gclid}`;
    if (fbpixel) return `fbpixel ${fbpixel}`;
    return null;
  }, [search]);

  // scroll to top of the page after going between step
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // check gclid and fbpixel on url and save it to localStorage
  useEffect(() => {
    const adInfo = parseAdInfo();
    if (adInfo) {
      localStorage.setItem("adInfo", adInfo);
    }
  }, [parseAdInfo]);

  // HANDLER FOR STEP 1
  const handleSubmitRegisterAndSelectProgram = async (values: any) => {
    try {
      setLoading(true);
      const { data: response } = await register({
        variables: parseRegistrationFormValues(values),
      });

      const {
        studentRegistration: { credentials, authenticatable: userData },
      } = response;

      // Store credentials and log user in
      authenticate(credentials, userData);
      // next step in registration flow
      setLoading(false);
      // store study language id
      setLanguageDataVar({
        studyLanguageId: values.currentStudyLanguage,
      });
      setStep(1);

      const activeClassType = CLASS_TYPES.find(
        (classType: { code: number; name: string }) => {
          return classType.name.toLowerCase().includes(values.classType);
        }
      );

      if (activeClassType) {
        await updateMySettings({
          variables: {
            input: {
              attributes: { preferClassType: activeClassType.code },
            },
          },
        });
      }
    } catch (e) {
      handleError(e);
      setLoading(false);
    }
  };

  // HANDLER FOR STEP 2
  const handleSubmitPaymentInfo = async (values: any) => {
    try {
      setLoading(true);

      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }
      // Get a reference to a mounted CardElement. Elements knows how
      // to find your CardElement because there can only ever be one of
      // each type of element.
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement as StripeCardElement,
      });

      if (error) {
        setLoading(false);
        handleError(new Error(t("Stripe could not process your card's info")));
      } else {
        const paymentMethodId = paymentMethod?.id;

        if (!paymentMethodId) return;

        // get adInfo from localStorage
        const adInfo = localStorage.getItem("adInfo") || null;

        // Create the subscription
        try {
          await createSubscription({
            priceId: chosenProgram.id,
            adInfo,
            paymentMethodId,
            promotionCode: values.promoCode,
          });

          setStep(2); // no need to do anything more
        } catch (e) {
          handleError(e);
        }
      }
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleClickBookAClass = () => {
    setLoggedIn(true);
    navigate("/book-class");
  };

  const handleToggleProgramSel = () => setProgramSelVisible(!programSelVisible);

  // Data for rendering
  const subscriptionEndDate =
    createSubscriptionResult?.createSubscription.subscription.endDate;
  const suscriptionInvoiceId =
    createSubscriptionResult?.createSubscription.subscription
      .stripeLatestInvoiceId;

  const scrollOption: Options = {
    behavior: "smooth",
    block: "center",
  };

  const renderSteps = () => {
    if (step === 0) {
      return (
        <Form
          name="registerAndSelectProgram"
          form={form}
          onFinish={handleSubmitRegisterAndSelectProgram}
          initialValues={{
            charactersPreference: "simplified",
          }}
          scrollToFirstError={scrollOption}
        >
          <Row gutter={20}>
            <Col>
              <RegistrationForm
                currentLanguageId={currentLanguageId}
                setCurrentLanguageId={(value) => setCurrentLanguageId(value)}
                form={form}
              />
            </Col>
            <Col>
              <SelectProgramForm
                chosenProgram={chosenProgram}
                setChosenProgram={setChosenProgram}
                loading={loading}
                showSubmitButton
                currentLanguageId={currentLanguageId}
                form={form}
              />
            </Col>
          </Row>
        </Form>
      );
    }

    if (step === 1) {
      return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Form name="payment" form={form} onFinish={handleSubmitPaymentInfo}>
            <Row gutter={20}>
              <Col>
                <PaymentForm
                  chosenProgram={chosenProgram}
                  loading={loading}
                  onToggleProgramSel={handleToggleProgramSel}
                  programSelVisible={programSelVisible}
                />
              </Col>
              <Col
                xs={{ span: 24 }}
                lg={{ span: 12 }}
                style={{ paddingRight: 0 }}
              >
                {programSelVisible && (
                  <SelectProgramForm
                    chosenProgram={chosenProgram}
                    setChosenProgram={setChosenProgram}
                    loading={createSubscriptionLoading}
                    showSubmitButton={false}
                    form={form}
                  />
                )}
                <ReviewPanel />
              </Col>
            </Row>
          </Form>
        </div>
      );
    }

    if (step === 2) {
      return (
        <Row gutter={20}>
          <Col>
            <Confirmation
              handleClickBookAClass={handleClickBookAClass}
              chosenProgram={chosenProgram}
              subscriptionEndDate={subscriptionEndDate}
            />
          </Col>
          <Col>
            <NewSubscriptionInvoice stripeInvoiceId={suscriptionInvoiceId} />
          </Col>
        </Row>
      );
    }
    return null;
  };

  return (
    <PageContent>
      <Col xs={24} sm={24} md={15}>
        <CustomSteps size="small" current={step} direction="horizontal">
          <CustomStep title={t("Courses")} />
          <CustomStep title={t("Payment")} />
          <CustomStep title={t("Learn")} />
        </CustomSteps>
      </Col>
      {errors && (
        <div>
          <br />
          <ErrorAlerting errors={errors} onClose={closeError} />
        </div>
      )}
      {renderSteps()}
    </PageContent>
  );
}

export default RegistrationCard;
